import { useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import logo from "../assets/images/sutramay-logo.svg";
import BrandSwitcher from "./BrandSwitcher";

const NAV_LINKS = [
  { name: "Home", href: "/" },
  { name: "Learn More", href: "/learn-more" },
  { name: "Catalogue", href: "/catalogue" },
  { name: "Contact Us", href: "/connectus" },
  { name: "Unit Locations", href: "/locations" },
];

const NAV_CLASS = "flex flex-nowrap items-center gap-x-4 sm:gap-x-[clamp(1rem,3vw,2.75rem)]";
const LINK_CLASS =
  "font-accent font-medium text-xs sm:text-xs tracking-[0.15em] uppercase text-maroon-700/90 hover:text-gold-700 transition-colors whitespace-nowrap";

export default function CustomNavbar({ overlay = false, showBrand = true }) {
  const headerClass = overlay
    ? "absolute inset-x-0 top-0 z-30 bg-transparent"
    : "bg-transparent";

  const rowRef = useRef(null);
  const brandRef = useRef(null);
  const measureRef = useRef(null);
  const [navFits, setNavFits] = useState(true);

  useLayoutEffect(() => {
    function checkFit() {
      const row = rowRef.current;
      const measure = measureRef.current;
      if (!row || !measure) return;

      const rowStyle = window.getComputedStyle(row);
      const paddingLeft = parseFloat(rowStyle.paddingLeft) || 0;
      const paddingRight = parseFloat(rowStyle.paddingRight) || 0;
      const contentWidth = row.clientWidth - paddingLeft - paddingRight;

      const brandWidth = showBrand && brandRef.current ? brandRef.current.offsetWidth : 0;
      const navWidth = measure.scrollWidth;
      const reserve = showBrand ? 32 : 16;

      setNavFits(contentWidth - brandWidth - reserve >= navWidth);
    }

    checkFit();

    const ro = new ResizeObserver(checkFit);
    if (rowRef.current) ro.observe(rowRef.current);

    window.addEventListener("resize", checkFit);
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(checkFit);
    }

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", checkFit);
    };
  }, [showBrand]);

  return (
    <header className={`relative ${headerClass}`}>
      <BrandSwitcher
        className={`absolute top-1.5 z-10 ${
          showBrand ? "right-6 sm:right-10 lg:right-16" : "left-6 sm:left-10 lg:left-16"
        }`}
      />

      <div
        ref={rowRef}
        className={`px-6 sm:px-10 lg:px-16 py-5 flex items-center ${
          showBrand ? "justify-between" : "justify-end"
        }`}
      >
        {showBrand && (
          <Link ref={brandRef} to="/" className="inline-flex items-center gap-2 sm:gap-3">
            <img src={logo} alt="Sutramay Industries" className="h-9 w-9 sm:h-14 sm:w-14" />
            <span className="font-heading font-bold text-xl sm:text-3xl tracking-wide text-maroon-700">
              Sutramay Industries
            </span>
          </Link>
        )}

        {navFits && (
          <nav className={NAV_CLASS} aria-label="Global">
            {NAV_LINKS.map((link) => (
              <Link key={link.href} to={link.href} className={LINK_CLASS}>
                {link.name}
              </Link>
            ))}
          </nav>
        )}
      </div>

      <nav
        ref={measureRef}
        aria-hidden="true"
        className={NAV_CLASS}
        style={{
          position: "absolute",
          visibility: "hidden",
          top: 0,
          left: 0,
          pointerEvents: "none",
          whiteSpace: "nowrap",
        }}
      >
        {NAV_LINKS.map((link) => (
          <span key={link.href} className={LINK_CLASS}>
            {link.name}
          </span>
        ))}
      </nav>
    </header>
  );
}
